/**
 * Utilitários para detecção de bandeira de cartão de crédito
 * Baseado nos padrões BIN (Bank Identification Number)
 */

/**
 * Mapeia os códigos de bandeira para o formato esperado pela Vindi
 */
export const CARD_BRANDS = {
  VISA: 'visa',
  MASTERCARD: 'mastercard',
  AMEX: 'american_express',
  ELO: 'elo',
  DINERS: 'diners_club',
  DISCOVER: 'discover',
  JCB: 'jcb',
  AURA: 'aura',
  HIPERCARD: 'hipercard',
};

/**
 * Padrões de BIN para cada bandeira
 * Baseado em: https://www.regular-expressions.info/creditcard.html
 */
const CARD_PATTERNS = {
  [CARD_BRANDS.VISA]: /^4[0-9]{0,15}$/,
  [CARD_BRANDS.MASTERCARD]: /^(5[1-5][0-9]{0,14}|2(2[2-9][0-9]{0,12}|[3-6][0-9]{0,13}|7[0-1][0-9]{0,12}|720[0-9]{0,11}))$/,
  [CARD_BRANDS.AMEX]: /^3[47][0-9]{0,13}$/,
  [CARD_BRANDS.DINERS]: /^3(?:0[0-5]|[68][0-9])[0-9]{0,11}$/,
  [CARD_BRANDS.DISCOVER]: /^6(?:011|5[0-9]{2})[0-9]{0,12}$/,
  [CARD_BRANDS.JCB]: /^(?:2131|1800|35\d{3})\d{0,11}$/,
  [CARD_BRANDS.ELO]: /^((((636368)|(438935)|(504175)|(451416)|(636297))\d{0,10})|((5067)|(4576)|(4011))\d{0,12})$/,
  [CARD_BRANDS.HIPERCARD]: /^(606282\d{10}(\d{3})?)|(3841\d{15})$/,
  [CARD_BRANDS.AURA]: /^50[0-9]{0,14}$/,
};

/**
 * Detecta a bandeira do cartão baseado no número
 * @param {string} cardNumber - Número do cartão (com ou sem espaços/traços)
 * @returns {string|null} Código da bandeira no formato Vindi ou null se não identificado
 */
export const detectCardBrand = (cardNumber) => {
  if (!cardNumber) return null;

  // Remove espaços, traços e outros caracteres não numéricos
  const cleanNumber = cardNumber.replace(/\D/g, '');

  // Testa cada padrão até encontrar uma correspondência
  for (const [brand, pattern] of Object.entries(CARD_PATTERNS)) {
    if (pattern.test(cleanNumber)) {
      return brand;
    }
  }

  return null;
};

/**
 * Valida se o número do cartão possui uma bandeira conhecida
 * @param {string} cardNumber - Número do cartão
 * @returns {boolean} True se a bandeira foi identificada
 */
export const hasValidCardBrand = (cardNumber) => {
  return detectCardBrand(cardNumber) !== null;
};

/**
 * Retorna o nome amigável da bandeira
 * @param {string} cardNumber - Número do cartão
 * @returns {string|null} Nome da bandeira para exibição
 */
export const getCardBrandName = (cardNumber) => {
  const brand = detectCardBrand(cardNumber);
  if (!brand) return null;

  const brandNames = {
    [CARD_BRANDS.VISA]: 'Visa',
    [CARD_BRANDS.MASTERCARD]: 'Mastercard',
    [CARD_BRANDS.AMEX]: 'American Express',
    [CARD_BRANDS.ELO]: 'Elo',
    [CARD_BRANDS.DINERS]: 'Diners Club',
    [CARD_BRANDS.DISCOVER]: 'Discover',
    [CARD_BRANDS.JCB]: 'JCB',
    [CARD_BRANDS.AURA]: 'Aura',
    [CARD_BRANDS.HIPERCARD]: 'Hipercard',
  };

  return brandNames[brand] || brand;
};

/**
 * Formata o número do cartão com espaços para exibição
 * @param {string} cardNumber - Número do cartão
 * @returns {string} Número formatado (ex: "1234 5678 9012 3456")
 */
export const formatCardNumber = (cardNumber) => {
  if (!cardNumber) return '';

  const cleanNumber = cardNumber.replace(/\D/g, '');
  const brand = detectCardBrand(cleanNumber);

  // American Express tem formato diferente: 1234 567890 12345
  if (brand === CARD_BRANDS.AMEX) {
    return cleanNumber.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  }

  // Diners Club: 1234 567890 1234
  if (brand === CARD_BRANDS.DINERS) {
    return cleanNumber.replace(/(\d{4})(\d{6})(\d{4})/, '$1 $2 $3');
  }

  // Formato padrão: 1234 5678 9012 3456
  return cleanNumber.replace(/(\d{4})/g, '$1 ').trim();
};

/**
 * Valida o comprimento do cartão baseado na bandeira
 * @param {string} cardNumber - Número do cartão
 * @returns {boolean} True se o comprimento está correto para a bandeira
 */
export const hasValidCardLength = (cardNumber) => {
  if (!cardNumber) return false;

  const cleanNumber = cardNumber.replace(/\D/g, '');
  const brand = detectCardBrand(cleanNumber);

  if (!brand) return false;

  const validLengths = {
    [CARD_BRANDS.VISA]: [13, 16, 19],
    [CARD_BRANDS.MASTERCARD]: [16],
    [CARD_BRANDS.AMEX]: [15],
    [CARD_BRANDS.DINERS]: [14],
    [CARD_BRANDS.DISCOVER]: [16],
    [CARD_BRANDS.JCB]: [16],
    [CARD_BRANDS.ELO]: [16],
    [CARD_BRANDS.HIPERCARD]: [16],
    [CARD_BRANDS.AURA]: [16],
  };

  return validLengths[brand]?.includes(cleanNumber.length) || false;
};

const cardBrandUtils = {
  detectCardBrand,
  hasValidCardBrand,
  getCardBrandName,
  formatCardNumber,
  hasValidCardLength,
  CARD_BRANDS,
};

export default cardBrandUtils;
